import React from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';

import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { propTypes } from '../../util/types';
import { getAuthPageUserType, isUserTypeAdmin, isValidUserType } from '../../util/data';
import { isPasswordRecoveryEmailNotFoundError } from '../../util/errors';
import { isScrollingDisabled } from '../../ducks/ui.duck';
import { useConfiguration } from '../../context/configurationContext';

import {
  LayoutSingleColumn,
  Page,
  Heading,
  AuthSection,
  NamedRedirect,
  NamedLink,
  IconArrowHead,
} from '../../components';

import TopbarContainer from '../../containers/TopbarContainer/TopbarContainer';
import NotFoundPage from '../NotFoundPage/NotFoundPage';

import PasswordRecoveryForm from './PasswordRecoveryForm/PasswordRecoveryForm';
import { recoverPassword, clearPasswordRecoveryError } from './PasswordRecoveryPage.duck';
import css from './PasswordRecoveryPage.module.css';

const getLoginLinkProps = userType =>
  userType
    ? {
        name: 'LoginForUserTypePage',
        params: { userType },
      }
    : {
        name: 'LoginPage',
      };

const PasswordRecovery = props => {
  const {
    initialEmail,
    onChange,
    onSubmitEmail,
    recoveryInProgress,
    recoveryError,
    userType,
  } = props;
  const loginLinkProps = getLoginLinkProps(userType);
  return (
    <div className={css.submitEmailContent}>
      <NamedLink {...loginLinkProps} className={css.backToLoginLink}>
        <IconArrowHead className={css.backToLoginIcon} direction="left" />
        <FormattedMessage id="PasswordRecoveryPage.backToLoginLinkText" />
      </NamedLink>
      <Heading as="h1" rootClassName={css.formTitle}>
        <FormattedMessage id="PasswordRecoveryPage.forgotPasswordTitle" />
      </Heading>
      <p className={css.formSubTitle}>
        <FormattedMessage id="PasswordRecoveryPage.forgotPasswordMessage" />
      </p>
      <PasswordRecoveryForm
        className={css.recoveryForm}
        inProgress={recoveryInProgress}
        onChange={onChange}
        onSubmit={values => onSubmitEmail(values.email)}
        initialValues={{ email: initialEmail }}
        recoveryError={recoveryError}
      />
    </div>
  );
};

const GenericError = () => {
  return (
    <div className={css.genericErrorContent}>
      <NamedLink name="LoginPage" className={css.backToLoginLink}>
        <IconArrowHead className={css.backToLoginIcon} direction="left" />
        <FormattedMessage id="PasswordRecoveryPage.backToLoginLinkText" />
      </NamedLink>
      <Heading as="h1" rootClassName={css.formTitle}>
        <FormattedMessage id="PasswordRecoveryPage.actionFailedTitle" />
      </Heading>
      <p className={css.formSubTitle}>
        <FormattedMessage id="PasswordRecoveryPage.actionFailedMessage" />
      </p>
    </div>
  );
};

export const PasswordRecoveryPageComponent = props => {
  const config = useConfiguration();
  const {
    scrollingDisabled,
    params: pathParams,
    location,
    initialEmail,
    submittedEmail,
    recoveryError,
    recoveryInProgress,
    passwordRequested,
    onChange,
    onSubmitEmail,
    intl,
  } = props;

  const { userTypes = [] } = config.user;

  const userType = getAuthPageUserType(config, location, pathParams);
  const validUserType = isValidUserType(userTypes, userType);
  const isAdminUserType = isUserTypeAdmin(userType);

  const show404 = userType && !validUserType;

  const showPasswordRecoveryForm = (
    <PasswordRecovery
      className={css.recoveryForm}
      initialEmail={initialEmail}
      onChange={onChange}
      onSubmitEmail={onSubmitEmail}
      recoveryInProgress={recoveryInProgress}
      recoveryError={recoveryError}
      userType={userType}
    />
  );

  if (passwordRequested && submittedEmail) {
    const redirectParams = {
      name: isAdminUserType ? 'LoginAdminPage' : 'LoginPage',
      state: {
        newPasswordRequested: true,
      },
    };
    return <NamedRedirect {...redirectParams} />;
  } else if (show404) {
    return <NotFoundPage staticContext={props.staticContext} />;
  }

  const sideTitleMsgId = isAdminUserType
    ? 'PasswordRecoveryPage.sideTitleAdmin'
    : 'PasswordRecoveryPage.sideTitle';

  return (
    <Page
      title={intl.formatMessage({
        id: 'PasswordRecoveryPage.title',
      })}
      scrollingDisabled={scrollingDisabled}
    >
      <LayoutSingleColumn mainColumnClassName={css.layoutWrapperMain} topbar={<TopbarContainer />}>
        <div className={css.root}>
          <AuthSection className={css.authSection} titleMsgId={sideTitleMsgId} />
          {isPasswordRecoveryEmailNotFoundError(recoveryError) ? (
            showPasswordRecoveryForm
          ) : recoveryError ? (
            <GenericError />
          ) : (
            showPasswordRecoveryForm
          )}
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

PasswordRecoveryPageComponent.defaultProps = {
  sendVerificationEmailError: null,
  initialEmail: null,
  recoveryError: null,
  passwordRequested: false,
};

const { bool, func, object, string, shape } = PropTypes;

PasswordRecoveryPageComponent.propTypes = {
  scrollingDisabled: bool.isRequired,
  initialEmail: string,
  recoveryError: propTypes.error,
  recoveryInProgress: bool.isRequired,
  passwordRequested: bool.isRequired,
  onChange: func.isRequired,
  onSubmitEmail: func.isRequired,

  // from withRouter
  location: shape({ state: object }).isRequired,

  // from injectIntl
  intl: intlShape.isRequired,
};

const mapStateToProps = state => {
  const {
    initialEmail,
    submittedEmail,
    recoveryError,
    recoveryInProgress,
    passwordRequested,
  } = state.PasswordRecoveryPage;
  return {
    scrollingDisabled: isScrollingDisabled(state),
    initialEmail,
    submittedEmail,
    recoveryError,
    recoveryInProgress,
    passwordRequested,
  };
};

const mapDispatchToProps = dispatch => ({
  onChange: () => dispatch(clearPasswordRecoveryError()),
  onSubmitEmail: email => dispatch(recoverPassword(email)),
});

const PasswordRecoveryPage = compose(
  withRouter,
  connect(
    mapStateToProps,
    mapDispatchToProps
  ),
  injectIntl
)(PasswordRecoveryPageComponent);

export default PasswordRecoveryPage;
